import React, { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { supabase } from '../../services/supabase';

const TokenExpiryWarning = ({ config }) => {
    const [expiringTokens, setExpiringTokens] = useState({ client: [], agent: [] });
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (config?.id !== 'individual') return;
        
        const checkTokenExpiry = async () => {
            setIsLoading(true);
            try {
                const now = new Date();
                const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

                // Check client tokens
                const { data: clientTokens, error: clientError } = await supabase
                    .from('hmrc_client_tokens')
                    .select('id, client_id, expires_at')
                    .lte('expires_at', sevenDaysFromNow.toISOString())
                    .gte('expires_at', now.toISOString());

                if (clientError) throw clientError;

                // Check agent token
                const { data: agentTokens, error: agentError } = await supabase
                    .from('hmrc_agent_tokens')
                    .select('id, environment, expires_at')
                    .lte('expires_at', sevenDaysFromNow.toISOString())
                    .gte('expires_at', now.toISOString());

                if (agentError) throw agentError;

                setExpiringTokens({
                    client: clientTokens || [],
                    agent: agentTokens || []
                });
            } catch (err) {
                console.error('Failed to check token expiry:', err);
            } finally {
                setIsLoading(false);
            }
        };

        checkTokenExpiry();
        // Re-check every 30 minutes
        const interval = setInterval(checkTokenExpiry, 30 * 60 * 1000);
        return () => clearInterval(interval);
    }, [config?.id]);

    const totalExpiring = expiringTokens.client.length + expiringTokens.agent.length;

    if (totalExpiring === 0) return null;

    return (
        <div className="px-3 py-2 bg-red-50 border border-red-200 flex items-center gap-3 text-xs">
            <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
            <div>
                <span className="font-semibold text-red-700">
                    {totalExpiring} token{totalExpiring !== 1 ? 's' : ''} expiring within 7 days
                </span>
                {expiringTokens.agent.length > 0 && (
                    <span className="block text-red-600">
                        • Agent token expires soon — reconnect to maintain HMRC access
                    </span>
                )}
                {expiringTokens.client.length > 0 && (
                    <span className="block text-red-600">
                        • {expiringTokens.client.length} client token{expiringTokens.client.length !== 1 ? 's' : ''} need renewal
                    </span>
                )}
            </div>
        </div>
    );
};

export default TokenExpiryWarning;
